import type {
  Fighter,
  FighterId,
  FighterSpriteState,
  FighterState,
  GameEvent,
  SpriteManifest,
} from "./game-types";
import { FIGHTER_IDS } from "./game-types";
import { getFighterConfig, usesDogPlaceholder } from "./fighter-config";
import {
  GameSimulation,
  PLATFORMS,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from "./simulation";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  drag: number;
  gravity: number;
  shape: "spark" | "leaf" | "ember";
}

interface Ring {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  radius: number;
  color: string;
  width: number;
}

const DEFAULT_PIVOTS: Record<FighterSpriteState, number> = {
  idle: 0.5,
  run: 0.5,
  attack: 0.5,
  jump: 0.5,
  special: 0.5,
  grab: 0.5,
  shield: 0.5,
  hurt: 0.5,
};

const PIVOTS = Object.fromEntries(
  FIGHTER_IDS.map((id) => [id, { ...DEFAULT_PIVOTS }]),
) as Record<FighterId, Record<FighterSpriteState, number>>;
PIVOTS.dog = {
  idle: 0.49,
  run: 0.51,
  attack: 0.47,
  jump: 0.52,
  special: 0.5,
  grab: 0.49,
  shield: 0.44,
  hurt: 0.54,
};
PIVOTS.tiger = {
  idle: 0.51,
  run: 0.52,
  attack: 0.64,
  jump: 0.53,
  special: 0.5,
  grab: 0.61,
  shield: 0.63,
  hurt: 0.52,
};

const BASE_FACING = Object.fromEntries(
  FIGHTER_IDS.map((id) => [id, 1]),
) as Record<FighterId, -1 | 1>;
BASE_FACING.tiger = -1;

export const RECOVERY_CAMERA_ZOOM = {
  min: 0.82,
  max: 0.88,
  hitMax: 0.945,
} as const;

export const FIGHTER_RENDER_SCALE: Record<FighterId, number> = {
  dog: 0.43,
  cat: 0.59,
  tiger: 0.415,
  dolphin: 0.54,
  horse: 0.55,
  lion: 0.56,
  elephant: 0.64,
  otter: 0.72,
  penguin: 0.68,
  wolf: 0.59,
  squirrel: 0.55,
  trex: 0.68,
};

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load image: ${url}`));
    image.src = url;
  });
}

function lerp(left: number, right: number, amount: number): number {
  return left + (right - left) * amount;
}

function seededNoise(seed: number): number {
  const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
}

function spriteStateFor(state: FighterState): FighterSpriteState {
  if (state === "ledge-hang") {
    return "jump";
  }
  if (state === "ledge-climb") {
    return "idle";
  }
  if (state === "ledge-attack") {
    return "attack";
  }
  if (state === "ledge-roll") {
    return "run";
  }
  return state;
}

export class GameRenderer {
  private readonly context: CanvasRenderingContext2D;
  private background: HTMLImageElement | null = null;
  private readonly fighterImages: Partial<
    Record<FighterId, Partial<Record<FighterSpriteState, HTMLImageElement>>>
  > = {};
  private readonly particles: Particle[] = [];
  private readonly rings: Ring[] = [];
  private shake = 0;
  private flash = 0;
  private zoomKick = 0;
  private time = 0;
  private cameraX = WORLD_WIDTH / 2;
  private cameraY = WORLD_HEIGHT / 2;
  private cameraZoom: number = RECOVERY_CAMERA_ZOOM.max;
  private readonly ambientLeaves = Array.from({ length: 23 }, (_, index) => ({
    x: seededNoise(index * 3.11) * WORLD_WIDTH,
    y: seededNoise(index * 4.27 + 2) * WORLD_HEIGHT,
    speed: 12 + seededNoise(index * 9.13) * 28,
    sway: 25 + seededNoise(index * 2.53) * 65,
    size: 3 + seededNoise(index * 7.47) * 6,
    phase: seededNoise(index * 1.33) * Math.PI * 2,
  }));

  constructor(canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d", {
      alpha: false,
      desynchronized: true,
    });
    if (!context) {
      throw new Error("Canvas 2D is unavailable.");
    }
    this.context = context;
    this.context.imageSmoothingEnabled = true;
    this.context.imageSmoothingQuality = "high";
  }

  async load(onProgress: (progress: number) => void): Promise<void> {
    const manifestUrl =
      new URLSearchParams(window.location.search).get("debug") === "fail-assets"
        ? "/assets/fighters/states/__missing-manifest__.json"
        : "/assets/fighters/states/manifest.json";
    const manifestResponse = await fetch(manifestUrl);
    const manifestType = manifestResponse.headers.get("content-type") ?? "";
    if (!manifestResponse.ok || !manifestType.includes("application/json")) {
      throw new Error("Fighter manifest failed to load.");
    }
    const manifest = (await manifestResponse.json()) as SpriteManifest;
    const jobs: Array<{
      fighter: FighterId;
      state: FighterSpriteState;
      url: string;
    }> = [];
    for (const fighter of FIGHTER_IDS) {
      const fighterManifest = manifest[fighter];
      if (!fighterManifest) {
        throw new Error(`Fighter manifest is missing ${fighter}.`);
      }
      this.fighterImages[fighter] = {};
      for (const state of Object.keys(
        fighterManifest,
      ) as FighterSpriteState[]) {
        jobs.push({ fighter, state, url: fighterManifest[state].file });
      }
    }

    const total = jobs.length + 1;
    let loaded = 0;
    this.background = await loadImage("/assets/stage/cloudbreak-canopy-bg-v2.webp");
    loaded += 1;
    onProgress(loaded / total);

    await Promise.all(
      jobs.map(async (job) => {
        const image = await loadImage(job.url);
        const collection = this.fighterImages[job.fighter];
        if (collection) {
          collection[job.state] = image;
        }
        loaded += 1;
        onProgress(loaded / total);
      }),
    );
  }

  handleEvent(event: GameEvent): void {
    if (event.type === "hit") {
      const color = this.fighterColor(event.attacker);
      this.shake = Math.min(30, 8 + event.strength * 0.018);
      this.flash = Math.min(1, 0.46 + event.damage * 0.025);
      this.zoomKick = Math.min(0.052, 0.016 + event.damage * 0.0019);
      this.spawnBurst(event.x, event.y, color, 21 + Math.round(event.damage * 0.7), 580);
      this.rings.push({
        x: event.x,
        y: event.y,
        life: 0.28,
        maxLife: 0.28,
        radius: 24,
        color,
        width: 11,
      });
      return;
    }
    if (event.type === "grab") {
      this.spawnBurst(event.x, event.y, "#ffdf88", 12, 350);
      this.rings.push({
        x: event.x,
        y: event.y,
        life: 0.38,
        maxLife: 0.38,
        radius: 18,
        color: "#ffdf88",
        width: 6,
      });
      return;
    }
    if (event.type === "ledge-grab") {
      const color = this.fighterColor(event.fighter);
      this.spawnBurst(event.x, event.y, color, 10, 240);
      this.rings.push({
        x: event.x,
        y: event.y,
        life: 0.32,
        maxLife: 0.32,
        radius: 16,
        color,
        width: 5,
      });
      return;
    }
    if (event.type === "throw") {
      this.shake = Math.max(this.shake, event.forward ? 12 : 16);
      this.spawnBurst(event.x, event.y, "#ffe29a", event.forward ? 18 : 24, 520);
      this.rings.push({
        x: event.x,
        y: event.y,
        life: 0.32,
        maxLife: 0.32,
        radius: event.forward ? 28 : 34,
        color: "#ffe29a",
        width: event.forward ? 9 : 12,
      });
      return;
    }
    if (event.type === "jump") {
      for (let index = 0; index < 7; index += 1) {
        const spread = (index - 3) * 18;
        this.particles.push({
          x: event.x + spread,
          y: event.y + 4,
          vx: spread * 1.4,
          vy: 20 + Math.abs(spread),
          life: 0.38,
          maxLife: 0.38,
          size: 9 + (index % 3) * 4,
          color: "rgba(221,239,226,.74)",
          drag: 4.8,
          gravity: -35,
          shape: "ember",
        });
      }
      return;
    }
    if (event.type === "special") {
      const color = this.fighterColor(event.fighter);
      this.spawnBurst(event.x, event.y, color, 14, 260);
      return;
    }
    if (event.type === "shield" || event.type === "shield-break") {
      const color = this.fighterColor(event.fighter);
      this.rings.push({
        x: event.x,
        y: event.y,
        life: event.type === "shield-break" ? 0.62 : 0.3,
        maxLife: event.type === "shield-break" ? 0.62 : 0.3,
        radius: event.type === "shield-break" ? 78 : 48,
        color,
        width: event.type === "shield-break" ? 18 : 8,
      });
      if (event.type === "shield-break") {
        this.shake = 18;
        this.spawnBurst(event.x, event.y, color, 34, 640);
      }
      return;
    }
    if (event.type === "ko") {
      this.shake = 32;
      this.flash = 1;
      this.zoomKick = 0.07;
      this.spawnBurst(
        Math.max(60, Math.min(WORLD_WIDTH - 60, event.x)),
        Math.max(60, Math.min(WORLD_HEIGHT - 60, event.y)),
        this.fighterColor(event.fighter),
        48,
        920,
      );
      return;
    }
    if (event.type === "respawn") {
      this.rings.push({
        x: event.x,
        y: event.y,
        life: 0.8,
        maxLife: 0.8,
        radius: 26,
        color: this.fighterColor(event.fighter),
        width: 8,
      });
    }
  }

  render(simulation: GameSimulation, alpha: number, delta: number): void {
    this.time += delta;
    this.updateEffects(delta);
    this.updateCamera(simulation, delta);
    const context = this.context;
    const shakeX = this.shake > 0.1 ? (Math.random() - 0.5) * this.shake : 0;
    const shakeY = this.shake > 0.1 ? (Math.random() - 0.5) * this.shake * 0.7 : 0;

    context.clearRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.drawCameraUnderlay();

    context.save();
    context.translate(WORLD_WIDTH / 2 + shakeX, WORLD_HEIGHT / 2 + shakeY);
    context.scale(this.cameraZoom, this.cameraZoom);
    context.translate(-this.cameraX, -this.cameraY);
    this.drawWorldBackdrop();
    this.drawAmbientLeaves();
    this.drawArenaAccents();
    this.drawProjectiles(simulation);
    for (const fighter of simulation.fighters) {
      this.drawFighterShadow(fighter);
    }
    this.drawGrabTether(simulation);
    for (const fighter of simulation.fighters) {
      this.drawFighter(fighter, alpha);
    }
    this.drawEffects();
    context.restore();
    this.drawForegroundGrade();

    if (this.flash > 0.01) {
      context.save();
      context.globalCompositeOperation = "screen";
      context.globalAlpha = this.flash * 0.26;
      context.fillStyle = "#fff8dc";
      context.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
      context.restore();
    }
  }

  private updateCamera(simulation: GameSimulation, delta: number): void {
    let targetX = WORLD_WIDTH / 2;
    let targetY = WORLD_HEIGHT / 2;
    let targetZoom: number = RECOVERY_CAMERA_ZOOM.max;

    if (simulation.status !== "title") {
      const activeFighters = simulation.fighters.filter(
        (fighter) => fighter.respawnTimer <= 0,
      );
      const framedFighters =
        activeFighters.length > 0
          ? activeFighters
          : simulation.fighters;
      const left = Math.min(...framedFighters.map((fighter) => fighter.position.x));
      const right = Math.max(...framedFighters.map((fighter) => fighter.position.x));
      const top = Math.min(
        ...framedFighters.map(
          (fighter) => fighter.position.y - fighter.config.height - 115,
        ),
      );
      const bottom = Math.max(
        ...framedFighters.map((fighter) => fighter.position.y + 35),
      );
      const fighterCenterX = (left + right) * 0.5;

      const widthNeeded = right - left + 540;
      const heightNeeded = bottom - top + 460;
      const framingPressure = Math.min(
        WORLD_WIDTH / widthNeeded,
        WORLD_HEIGHT / heightNeeded,
      );
      targetZoom = Math.max(
        RECOVERY_CAMERA_ZOOM.min,
        Math.min(RECOVERY_CAMERA_ZOOM.max, framingPressure),
      );

      const halfViewWidth = WORLD_WIDTH / (2 * targetZoom);
      const halfViewHeight = WORLD_HEIGHT / (2 * targetZoom);
      targetX = Math.max(
        -220 + halfViewWidth,
        Math.min(2140 - halfViewWidth, fighterCenterX),
      );

      const topCameraLimit = top + 400 / targetZoom;
      const bottomCameraLimit = bottom - 320 / targetZoom;
      targetY = Math.max(
        bottomCameraLimit,
        Math.min(topCameraLimit, WORLD_HEIGHT / 2),
      );
      targetY = Math.max(
        -190 + halfViewHeight,
        Math.min(1204 - halfViewHeight, targetY),
      );
    }

    targetZoom = Math.min(
      RECOVERY_CAMERA_ZOOM.hitMax,
      targetZoom + this.zoomKick,
    );
    const smoothing = delta > 0 ? 1 - Math.exp(-delta * 5.2) : 0;
    this.cameraX = lerp(this.cameraX, targetX, smoothing);
    this.cameraY = lerp(this.cameraY, targetY, smoothing);
    this.cameraZoom = lerp(this.cameraZoom, targetZoom, smoothing);
  }

  private drawCameraUnderlay(): void {
    this.context.fillStyle = "#06101a";
    this.context.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
  }

  private drawWorldBackdrop(): void {
    if (!this.background) {
      return;
    }
    const context = this.context;
    context.drawImage(this.background, -220, -124, 2360, 1328);

    const bloom = context.createRadialGradient(1242, 189, 10, 1242, 189, 836);
    bloom.addColorStop(0, `rgba(255,235,177,${0.12 + Math.sin(this.time * 0.55) * 0.02})`);
    bloom.addColorStop(0.38, "rgba(255,195,104,.035)");
    bloom.addColorStop(1, "rgba(255,186,90,0)");
    context.fillStyle = bloom;
    context.fillRect(300, -124, 1840, 1100);

    const haze = context.createLinearGradient(0, 503, 0, 1007);
    haze.addColorStop(0, "rgba(149,211,233,0)");
    haze.addColorStop(0.63, `rgba(174,220,237,${0.035 + Math.sin(this.time * 0.31) * 0.008})`);
    haze.addColorStop(1, "rgba(41,91,117,.12)");
    context.fillStyle = haze;
    context.fillRect(-220, 465, 2360, 615);
  }

  private drawAmbientLeaves(): void {
    const context = this.context;
    context.save();
    for (const leaf of this.ambientLeaves) {
      const y = (leaf.y + this.time * leaf.speed) % (WORLD_HEIGHT + 100) - 50;
      const x = leaf.x + Math.sin(this.time * 0.7 + leaf.phase) * leaf.sway;
      context.save();
      context.translate(x, y);
      context.rotate(this.time * 0.45 + leaf.phase);
      context.globalAlpha = 0.18 + (leaf.size / 9) * 0.28;
      context.fillStyle = leaf.phase > Math.PI ? "#e6b657" : "#6fbf83";
      context.beginPath();
      context.ellipse(0, 0, leaf.size * 1.8, leaf.size * 0.62, 0, 0, Math.PI * 2);
      context.fill();
      context.restore();
    }
    context.restore();
  }

  private drawArenaAccents(): void {
    const context = this.context;
    const pulse = 0.6 + Math.sin(this.time * 2.2) * 0.18;
    for (const [x, y] of [
      [515, 114],
      [626, 225],
      [1280, 116],
      [1375, 230],
    ] as const) {
      const glow = context.createRadialGradient(x, y, 2, x, y, 34);
      glow.addColorStop(0, `rgba(109,255,225,${pulse})`);
      glow.addColorStop(0.2, "rgba(38,236,213,.24)");
      glow.addColorStop(1, "rgba(38,236,213,0)");
      context.fillStyle = glow;
      context.fillRect(x - 40, y - 40, 80, 80);
    }
  }

  private drawFighterShadow(fighter: Fighter): void {
    if (fighter.respawnTimer > 0) {
      return;
    }
    const platform = PLATFORMS.filter(
      (candidate) =>
        fighter.position.x >= candidate.x1 - 70 &&
        fighter.position.x <= candidate.x2 + 70 &&
        candidate.y >= fighter.position.y,
    ).sort((left, right) => left.y - right.y)[0];
    if (!platform || platform.y - fighter.position.y > 420) {
      return;
    }
    const distance = Math.max(0, platform.y - fighter.position.y);
    const strength = Math.max(0.06, 0.34 - distance / 1400);
    const width = fighter.config.width * 1.25 * (1 - distance / 1200);
    const context = this.context;
    context.save();
    context.globalAlpha = strength;
    context.filter = "blur(10px)";
    context.fillStyle = "#071013";
    context.beginPath();
    context.ellipse(fighter.position.x, platform.y + 8, width, 22, 0, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }

  private drawFighter(fighter: Fighter, alpha: number): void {
    if (fighter.respawnTimer > 0) {
      return;
    }
    const spriteState = spriteStateFor(fighter.state);
    const image = this.fighterImages[fighter.id]?.[spriteState];
    if (!image) {
      return;
    }

    const x = lerp(fighter.previousPosition.x, fighter.position.x, alpha);
    const y = lerp(fighter.previousPosition.y, fighter.position.y, alpha);
    const baseScale = FIGHTER_RENDER_SCALE[fighter.id];
    const motion = Math.abs(fighter.velocity.x);
    const bob =
      fighter.state === "idle"
        ? Math.sin(
            this.time * (usesDogPlaceholder(fighter.id) ? 3.2 : 2.45),
          ) * 4
        : fighter.state === "run"
          ? Math.sin(this.time * 14) * 7
          : 0;
    const squashX =
      fighter.state === "run" ? 1 + Math.min(0.07, motion / 10_000) : 1;
    const squashY = fighter.state === "run" ? 0.97 : 1;
    const pivot = PIVOTS[fighter.id][spriteState];
    const flip = fighter.facing === BASE_FACING[fighter.id] ? 1 : -1;
    const heldJitter = fighter.grabbedBy ? Math.sin(this.time * 37) * 2.4 : 0;
    const heldTilt = fighter.grabbedBy
      ? Math.sin(this.time * 19) * 0.025 - flip * 0.08
      : 0;
    const context = this.context;

    if (fighter.state === "run" && motion > 420) {
      for (let trail = 2; trail >= 1; trail -= 1) {
        context.save();
        context.globalAlpha = 0.07 * trail;
        context.translate(x - Math.sign(fighter.velocity.x) * trail * 29, y + bob);
        context.scale(flip * baseScale * squashX, baseScale * squashY);
        context.filter =
          usesDogPlaceholder(fighter.id)
            ? "saturate(1.5) hue-rotate(-8deg)"
            : "saturate(1.35)";
        context.drawImage(image, -pivot * image.width, -image.height);
        context.restore();
      }
    }

    context.save();
    context.translate(x, y + bob + heldJitter);
    context.rotate(heldTilt);
    context.scale(flip * baseScale * squashX, baseScale * squashY);
    context.globalAlpha =
      fighter.invulnerability > 0 && Math.sin(this.time * 31) > 0.22 ? 0.43 : 1;
    if (fighter.flashTimer > 0) {
      context.filter = "brightness(2.4) saturate(.1)";
      context.shadowColor = "#ffffff";
      context.shadowBlur = 34;
    } else if (fighter.grabbedBy) {
      context.filter =
        "drop-shadow(0 0 11px rgba(255,226,154,.92)) drop-shadow(0 9px 6px rgba(0,0,0,.27))";
      context.shadowColor = "rgba(255,226,154,.7)";
      context.shadowBlur = 22;
    } else {
      context.filter = "drop-shadow(0 9px 6px rgba(0,0,0,.27))";
      context.shadowColor = this.hexToRgba(fighter.config.accent, 0.22);
      context.shadowBlur = 15 + fighter.damage * 0.035;
    }
    context.drawImage(image, -pivot * image.width, -image.height);
    context.restore();

    if (fighter.invulnerability > 0) {
      const radius = fighter.config.width * 1.25;
      const aura = context.createRadialGradient(x, y - 112, 12, x, y - 112, radius);
      aura.addColorStop(0, "rgba(255,255,255,.04)");
      aura.addColorStop(
        0.76,
        this.hexToRgba(fighter.config.accent, 0.055),
      );
      aura.addColorStop(1, "rgba(255,255,255,0)");
      context.fillStyle = aura;
      context.fillRect(x - radius, y - 112 - radius, radius * 2, radius * 2);
    }
  }

  private drawGrabTether(simulation: GameSimulation): void {
    const attacker = simulation.fighters.find(
      (fighter) => fighter.heldFighter !== null,
    );
    if (!attacker?.heldFighter) {
      return;
    }
    const defender = simulation.getFighter(attacker.heldFighter);
    const startX = attacker.position.x + attacker.facing * 26;
    const startY = attacker.position.y - attacker.config.height * 0.54;
    const endX = defender.position.x;
    const endY = defender.position.y - defender.config.height * 0.5;
    const middleX = (startX + endX) * 0.5;
    const middleY = Math.min(startY, endY) - 34;
    const context = this.context;

    context.save();
    context.globalCompositeOperation = "screen";
    context.lineCap = "round";
    context.shadowColor = "#ffe29a";
    context.shadowBlur = 18;

    for (const [width, alpha] of [
      [18, 0.11],
      [8, 0.36],
      [3, 0.92],
    ] as const) {
      context.strokeStyle = `rgba(255,226,154,${alpha})`;
      context.lineWidth = width;
      context.beginPath();
      context.moveTo(startX, startY);
      context.quadraticCurveTo(middleX, middleY, endX, endY);
      context.stroke();
    }

    const pulse = 30 + Math.sin(this.time * 16) * 6;
    context.strokeStyle = "rgba(255,238,181,.88)";
    context.lineWidth = 4;
    context.beginPath();
    context.arc(endX, endY, pulse, 0, Math.PI * 2);
    context.stroke();
    context.restore();
  }

  private drawProjectiles(simulation: GameSimulation): void {
    const context = this.context;
    for (const projectile of simulation.projectiles) {
      context.save();
      context.globalCompositeOperation = "screen";
      for (let trail = 4; trail >= 1; trail -= 1) {
        const x = projectile.position.x - projectile.velocity.x * trail * 0.018;
        const radius = projectile.radius * (1 - trail * 0.1);
        context.globalAlpha = 0.06 + (4 - trail) * 0.025;
        context.fillStyle = projectile.color;
        context.beginPath();
        context.arc(x, projectile.position.y, radius, 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 1;
      context.shadowColor = projectile.color;
      context.shadowBlur = 34;
      const gradient = context.createRadialGradient(
        projectile.position.x - projectile.radius * 0.2,
        projectile.position.y - projectile.radius * 0.2,
        2,
        projectile.position.x,
        projectile.position.y,
        projectile.radius,
      );
      gradient.addColorStop(0, "#ffffff");
      gradient.addColorStop(0.28, projectile.color);
      gradient.addColorStop(1, "rgba(35,71,91,.04)");
      context.fillStyle = gradient;
      context.beginPath();
      context.arc(
        projectile.position.x,
        projectile.position.y,
        projectile.radius,
        0,
        Math.PI * 2,
      );
      context.fill();
      context.restore();
    }
  }

  private spawnBurst(
    x: number,
    y: number,
    color: string,
    count: number,
    speed: number,
  ): void {
    for (let index = 0; index < count; index += 1) {
      const angle = (index / count) * Math.PI * 2 + Math.random() * 0.24;
      const velocity = speed * (0.36 + Math.random() * 0.74);
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        life: 0.22 + Math.random() * 0.34,
        maxLife: 0.56,
        size: 3 + Math.random() * 10,
        color,
        drag: 3.4,
        gravity: 380,
        shape: index % 5 === 0 ? "ember" : "spark",
      });
    }
  }

  private fighterColor(id: FighterId): string {
    return getFighterConfig(id).accent;
  }

  private hexToRgba(hex: string, alpha: number): string {
    const value = hex.replace("#", "");
    const red = Number.parseInt(value.slice(0, 2), 16);
    const green = Number.parseInt(value.slice(2, 4), 16);
    const blue = Number.parseInt(value.slice(4, 6), 16);
    return `rgba(${red},${green},${blue},${alpha})`;
  }

  private updateEffects(delta: number): void {
    this.shake = Math.max(0, this.shake - 75 * delta);
    this.flash = Math.max(0, this.flash - 6.8 * delta);
    this.zoomKick = Math.max(0, this.zoomKick - 0.24 * delta);
    for (let index = this.particles.length - 1; index >= 0; index -= 1) {
      const particle = this.particles[index];
      if (!particle) {
        continue;
      }
      particle.life -= delta;
      if (particle.life <= 0) {
        this.particles.splice(index, 1);
        continue;
      }
      const damp = Math.exp(-particle.drag * delta);
      particle.vx *= damp;
      particle.vy = particle.vy * damp + particle.gravity * delta;
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
    }
    for (let index = this.rings.length - 1; index >= 0; index -= 1) {
      const ring = this.rings[index];
      if (!ring) {
        continue;
      }
      ring.life -= delta;
      if (ring.life <= 0) {
        this.rings.splice(index, 1);
      }
    }
  }

  private drawEffects(): void {
    const context = this.context;
    context.save();
    context.globalCompositeOperation = "screen";
    for (const ring of this.rings) {
      const progress = 1 - ring.life / ring.maxLife;
      context.globalAlpha = (1 - progress) * 0.82;
      context.strokeStyle = ring.color;
      context.lineWidth = ring.width * (1 - progress * 0.65);
      context.shadowColor = ring.color;
      context.shadowBlur = 22;
      context.beginPath();
      context.arc(ring.x, ring.y, ring.radius + progress * 145, 0, Math.PI * 2);
      context.stroke();
    }
    for (const particle of this.particles) {
      const alpha = Math.max(0, particle.life / particle.maxLife);
      context.globalAlpha = alpha;
      context.fillStyle = particle.color;
      context.strokeStyle = particle.color;
      context.shadowColor = particle.color;
      context.shadowBlur = particle.size * 2;
      if (particle.shape === "spark") {
        const length = particle.size * (2.8 + alpha * 3);
        const magnitude = Math.hypot(particle.vx, particle.vy) || 1;
        context.lineWidth = Math.max(1.5, particle.size * 0.45);
        context.beginPath();
        context.moveTo(particle.x, particle.y);
        context.lineTo(
          particle.x - (particle.vx / magnitude) * length,
          particle.y - (particle.vy / magnitude) * length,
        );
        context.stroke();
      } else {
        context.beginPath();
        context.arc(particle.x, particle.y, particle.size * alpha, 0, Math.PI * 2);
        context.fill();
      }
    }
    context.restore();
  }

  private drawForegroundGrade(): void {
    const context = this.context;
    const vignette = context.createRadialGradient(
      WORLD_WIDTH / 2,
      WORLD_HEIGHT * 0.47,
      WORLD_HEIGHT * 0.17,
      WORLD_WIDTH / 2,
      WORLD_HEIGHT * 0.49,
      WORLD_WIDTH * 0.7,
    );
    vignette.addColorStop(0, "rgba(0,0,0,0)");
    vignette.addColorStop(0.72, "rgba(2,9,13,.04)");
    vignette.addColorStop(1, "rgba(1,6,10,.38)");
    context.fillStyle = vignette;
    context.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    const bottom = context.createLinearGradient(0, 810, 0, WORLD_HEIGHT);
    bottom.addColorStop(0, "rgba(1,8,12,0)");
    bottom.addColorStop(1, "rgba(1,8,12,.23)");
    context.fillStyle = bottom;
    context.fillRect(0, 800, WORLD_WIDTH, WORLD_HEIGHT - 800);
  }
}
